import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { AtomIcon, CursorClickIcon, Link as LinkIcon, SquareSlashIcon, Team, Trash } from '@air/next-icons';

import { WorkspaceSidebarCreativePerformance } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCreativePerformance';
import { WorkspaceSidebarNavigationItem } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItem';
import { WorkspaceSidebarNavigationItemNotifications } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItemNotifications';
import { WorkspaceSidebarNavigationItemSearch } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItemSearch';
import { WorkspaceSidebarNavigationItemUpsell } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItemUpsell';
import { Pathnames } from '~/components/v2/constants/pathnames';
import { usePrivateContextSourcesPermissions } from '~/components/v2/hooks/usePrivateContextSourcesPermissions';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsCreativePerformanceEnabled } from '~/hooks/useIsCreativePerformanceEnabled';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';
import { canViewCreativePerformance, canViewPeople, canViewSkills } from '~/utils/permissions/workspacePermissions';

export const WorkspaceSidebarNavigation = memo(() => {
  const router = useRouter();
  const { collectionsEnabled } = useFeatureFlags();
  const { enableSkillsUi, generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const { libraries } = usePrivateAddOnFeatures();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { canView: canViewContextSources } = usePrivateContextSourcesPermissions();
  const { canViewAddOn: canSeePrivatePeople } = useCanViewPrivateAddOn({ key: 'facialRecognition' });
  const { canViewAddOn: canViewVideoIntelligence } = useCanViewPrivateAddOn({ key: 'videoIntelligence' });
  const { isEnabled: isCreativePerformanceEnabled } = useIsCreativePerformanceEnabled();
  const canViewCreativePerformancePage = canViewCreativePerformance(workspacePermissions);
  const canViewSkillsPage = canViewSkills(workspacePermissions);

  const { trackSidebarInteraction } = useTrackSidebarInteraction();
  const { selectCanvasesView } = useSelectedCanvasId();
  const canSeePeople = canViewPeople(workspacePermissions) && canSeePrivatePeople;
  const showUpsell = !libraries && !canSeePrivatePeople && !canViewVideoIntelligence;

  const handlePeopleClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'people' });
  }, [trackSidebarInteraction]);

  const handleRecentlyDeletedClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'recently_deleted' });
  }, [trackSidebarInteraction]);

  const handleShareLinksClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'share_links' });
  }, [trackSidebarInteraction]);

  const handleMyCanvasesClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'canvases' });
    selectCanvasesView('my-canvases');
  }, [trackSidebarInteraction, selectCanvasesView]);

  const handleSkillsClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'skills' });
  }, [trackSidebarInteraction]);

  const handleContextSourcesClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: 'context_sources' });
  }, [trackSidebarInteraction]);

  return (
    <div className="group/workspace-sidebar-navigation flex flex-col">
      <WorkspaceSidebarNavigationItemSearch />

      <WorkspaceSidebarNavigationItemNotifications />

      {canSeePeople && (
        <WorkspaceSidebarNavigationItem
          asChild
          data-selected={router.pathname === Pathnames.People}
          onClick={handlePeopleClick}
        >
          <Link href={{ pathname: Pathnames.People, query: getCanvasQueryParams() }}>
            <Team className="size-4 shrink-0" />
            People
          </Link>
        </WorkspaceSidebarNavigationItem>
      )}

      {generativeCanvasEnabled && (
        <WorkspaceSidebarNavigationItem
          data-selected={router.pathname === Pathnames.MyCanvases}
          onClick={handleMyCanvasesClick}
        >
          <CursorClickIcon className="size-4 shrink-0" />
          Canvases
        </WorkspaceSidebarNavigationItem>
      )}

      {generativeCanvasEnabled && canViewContextSources && (
        <WorkspaceSidebarNavigationItem
          asChild
          data-selected={router.pathname === Pathnames.ContextSources}
          onClick={handleContextSourcesClick}
        >
          <Link href={{ pathname: Pathnames.ContextSources, query: getCanvasQueryParams() }}>
            <AtomIcon className="size-4 shrink-0" />
            Context
          </Link>
        </WorkspaceSidebarNavigationItem>
      )}

      {enableSkillsUi && canViewSkillsPage && (
        <WorkspaceSidebarNavigationItem
          asChild
          data-selected={router.pathname === Pathnames.Skills}
          onClick={handleSkillsClick}
        >
          <Link href={{ pathname: Pathnames.Skills, query: getCanvasQueryParams() }}>
            <SquareSlashIcon className="size-4 shrink-0" />
            Skills
          </Link>
        </WorkspaceSidebarNavigationItem>
      )}

      {isCreativePerformanceEnabled && canViewCreativePerformancePage && <WorkspaceSidebarCreativePerformance />}

      {collectionsEnabled && (
        <WorkspaceSidebarNavigationItem
          asChild
          data-selected={router.pathname === Pathnames.ShareLinks}
          onClick={handleShareLinksClick}
        >
          <Link href={{ pathname: Pathnames.ShareLinks, query: getCanvasQueryParams() }}>
            <LinkIcon className="size-4 shrink-0" />
            Share links
          </Link>
        </WorkspaceSidebarNavigationItem>
      )}

      <WorkspaceSidebarNavigationItem
        asChild
        data-selected={router.pathname === Pathnames.RecentlyDeleted}
        onClick={handleRecentlyDeletedClick}
      >
        <Link href={{ pathname: Pathnames.RecentlyDeleted }}>
          <Trash className="size-4 shrink-0" />
          Recently deleted
        </Link>
      </WorkspaceSidebarNavigationItem>

      {showUpsell && <WorkspaceSidebarNavigationItemUpsell />}
    </div>
  );
});

WorkspaceSidebarNavigation.displayName = 'WorkspaceSidebarNavigation';
